import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import i18nInstance from './i18n'

/**
 * Central locale-aware date/number formatting.
 *
 * All of these delegate to the platform `Intl` / `Date#toLocale*` APIs but pass
 * the *active UI locale* (`i18n.language`) explicitly instead of relying on the
 * browser/runtime default. Passing the locale is the only behavioural change —
 * the formatting options (and therefore the displayed content: which fields
 * show, 12/24h, etc.) are preserved from each call site.
 *
 * Two entry points:
 *  - `useLocaleFormat()` — a hook for React components (reactive to locale change).
 *  - `formatDate` / `formatTime` / `formatNumber` — plain functions that accept
 *    an explicit `locale`, for module-level (non-component) code. They default
 *    to the current `i18n.language` singleton when no locale is supplied.
 */

export type LocaleFormatLocale = string | undefined

function resolveLocale(locale?: LocaleFormatLocale): string | undefined {
  // Undefined tells Intl/toLocale* to use the runtime default; we instead want
  // the active i18n language. An explicit locale (e.g. from a hook) wins.
  return locale ?? i18nInstance.language ?? undefined
}

export function formatDate(
  value: Date | string | number | null | undefined,
  options?: Intl.DateTimeFormatOptions,
  locale?: LocaleFormatLocale,
): string {
  if (value == null) return ''
  const d = value instanceof Date ? value : new Date(value)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleDateString(resolveLocale(locale), options)
}

export function formatTime(
  value: Date | string | number | null | undefined,
  options?: Intl.DateTimeFormatOptions,
  locale?: LocaleFormatLocale,
): string {
  if (value == null) return ''
  const d = value instanceof Date ? value : new Date(value)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleTimeString(resolveLocale(locale), options)
}

export function formatDateTime(
  value: Date | string | number | null | undefined,
  options?: Intl.DateTimeFormatOptions,
  locale?: LocaleFormatLocale,
): string {
  if (value == null) return ''
  const d = value instanceof Date ? value : new Date(value)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleString(resolveLocale(locale), options)
}

export function formatNumber(
  value: number | null | undefined,
  options?: Intl.NumberFormatOptions,
  locale?: LocaleFormatLocale,
): string {
  if (value == null || (typeof value === 'number' && isNaN(value))) return ''
  return value.toLocaleString(resolveLocale(locale), options)
}

export interface LocaleFormatter {
  locale: string | undefined
  formatDate: (value: Date | string | number | null | undefined, options?: Intl.DateTimeFormatOptions) => string
  formatTime: (value: Date | string | number | null | undefined, options?: Intl.DateTimeFormatOptions) => string
  formatDateTime: (value: Date | string | number | null | undefined, options?: Intl.DateTimeFormatOptions) => string
  formatNumber: (value: number | null | undefined, options?: Intl.NumberFormatOptions) => string
}

/**
 * Hook returning locale-aware formatters bound to the active UI locale.
 * Re-computes when the language changes.
 */
export function useLocaleFormat(): LocaleFormatter {
  const { i18n } = useTranslation()
  const locale = i18n.language

  return useMemo<LocaleFormatter>(
    () => ({
      locale,
      formatDate: (value, options) => formatDate(value, options, locale),
      formatTime: (value, options) => formatTime(value, options, locale),
      formatDateTime: (value, options) => formatDateTime(value, options, locale),
      formatNumber: (value, options) => formatNumber(value, options, locale),
    }),
    [locale],
  )
}

export default useLocaleFormat
